import React, { useEffect } from 'react';
import { Tooltip, TooltipPlacement } from '../../../../components/atoms/Tooltip';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import useCollapsibleOverlay from '../../../../hooks/useCollapsibleOverlay';
import { CollapsibleContentSpan } from '../../../../components/organisms/CollapsibleOverlay/CollapsibleOverlay';
import { Modifier, modifierToArray } from '../../../../lib/bemUtils/Modifier';
import { appendClassNames } from '../../../../lib/bemUtils/classname';

export enum HelpModifier {
  NOPRINT = 'noprint',
}

export type Help = {
  id: string;
  helpText: string;
  tooltipPlacement?: TooltipPlacement;
  modifier?: Modifier<HelpModifier>;
};

/**
 * Renders a button in a Label with a Tooltip and a Collapsible with the HelpText inside.
 */
export function Help({ id, helpText, tooltipPlacement = TooltipPlacement.RIGHT, modifier }: Help) {
  const { t } = useSonTranslation('FormHelp');
  const { toggleProps, collapsibleProps, collapsibleOpen, popperInstance } = useCollapsibleOverlay('bottom');

  useEffect(() => {
    if (collapsibleOpen && popperInstance) {
      popperInstance.update();
    }
  });

  return (
    <span id={id} className={appendClassNames('help', ...modifierToArray(modifier).map(m => m ?? undefined))}>
      <span className="help__text-container">
        <Tooltip attachTo={toggleProps.ref} placement={tooltipPlacement} hideOnClick>
          <button
            type="button"
            className="help__button font-bold-15-15"
            title={t('help')}
            aria-label={t('help')}
            {...toggleProps}
          >
            <span aria-hidden="true">?</span>
          </button>
        </Tooltip>
        <CollapsibleContentSpan {...collapsibleProps}>
          {collapsibleOpen && <span className="help__text">{helpText}</span>}
        </CollapsibleContentSpan>
      </span>
    </span>
  );
}
